import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, Form, Row} from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { isNaturalNumber } from '../utils/jsUtils';
import configInfo from '../config/config.json';
import { formatDate } from '../utils/timeUtils';


const LocationAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [locationAttrs, setLocationAttrs] = useState({
    id: '',
    code: '',
    description: '',
    site: '',
    building: '',
    wing: '',
    floor: '',
    room: '',
    roomType: '',
    roomSeq: null,
    externalSystemId: ''
  });

  const [currentLocationAttrs, setCurrentLocationAttrs] = useState({});

  const typeList = [
    { key: "CITY",     value: "CITY"     },
    { key: "DISTRICT", value: "DISTRICT" },
    { key: "NBHD",     value: "NBHD"     },
    { key: "SITE",     value: "SITE"     },
    { key: "BUILDING", value: "BUILDING" },
    { key: "WING",     value: "WING"     },
    { key: "FLOOR",    value: "FLOOR"    },
    { key: "ROOM",     value: "ROOM"     },
    { key: "CORRIDOR", value: "CORRIDOR" },
    { key: "ST",       value: "ST"       },
    { key: "AREA",     value: "AREA"     },
    { key: "CTYD",     value: "CTYD"     },
    { key: "BLDG_DR",  value: "BLDG_DR"  },
    { key: "INT_DR",   value: "INT_DR"   },
    { key: "LIFT",     value: "LIFT"     },
    { key: "OTHER",    value: "OTHER"    }
  ];

  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setLocationAttrs(props.objDetails);
      setCurrentLocationAttrs(props.objDetails);
    }
  }, [props]);

  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg, setErrMsg] = useState('');


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setLocationAttrs({ ...locationAttrs, [e.target.name]: e.target.value })
  }

  const onTypeClick = (key) => {
    setLocationAttrs({ ...locationAttrs, type: key })
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setLocationAttrs(currentLocationAttrs);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate parameters
    if (!locationAttrs.description || locationAttrs.description.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
    } else if (locationAttrs.description.length > 200) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + "200");

    } else if (locationAttrs.name && (locationAttrs.name.length > 100)) {
      setErrMsg(t("I18N_ERR_Max_len_name") + "100");

    } else if (locationAttrs.nameLang2 && (locationAttrs.nameLang2.length > 100)) {
      setErrMsg(t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ") - " + t("I18N_ERR_Max_len") + "100");

    } else if (locationAttrs.nameLang3 && (locationAttrs.nameLang3.length > 100)) {
      setErrMsg(t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ") - " + t("I18N_ERR_Max_len") + "100");

    } else if (!locationAttrs.type || locationAttrs.description.type === 0) {
      setErrMsg(t("I18N_ERR_Need_type"));

    } else if (locationAttrs.site && (locationAttrs.site.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_site") + "30");

    } else if (locationAttrs.building && (locationAttrs.building.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_building") + "30");

    } else if (locationAttrs.wing && (locationAttrs.wing.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_wing") + "30");

    } else if (locationAttrs.floor && (locationAttrs.floor.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_floor") + "30");

    } else if (locationAttrs.room && (locationAttrs.room.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_room") + "30");

    } else if (locationAttrs.roomType && (locationAttrs.roomType.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_room_type") + "30");

    } else if (locationAttrs.roomSeq && (locationAttrs.roomSeq.length > 0) &&
               (!isNaturalNumber(locationAttrs.roomSeq) || (locationAttrs.roomSeq > 2147483647))) {
      setErrMsg(t("I18N_Room_seq") + t("I18N_ERR_Must_be_pos_int_and_LT") + '2,147,483,648.');
      return;

    } else if (locationAttrs.externalSystemId && (locationAttrs.externalSystemId.length > 30)) {
      setErrMsg(t("I18N_ERR_Max_len_ext_sys_id") + "30");

    } else {
      updateLocation();
    }
  }

  const updateLocation = () => {

    const reqBody = locationAttrs;

    // Convert roomSeq into integer type
    reqBody.roomSeq = locationAttrs.roomSeq && (locationAttrs.roomSeq.length > 0) ? (locationAttrs.roomSeq * 1.0) : null;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.put("locations/" + locationAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          refreshLocationAttrs(locationAttrs.id);

          // Restore UI to viewing mode
          setEditMode(false);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });

  }

  const refreshLocationAttrs = (locationId) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("locations/" + locationId);

        resolve(res.data);

        if (res.status === 200) {
          setLocationAttrs(res.data[0]);
          setCurrentLocationAttrs(res.data[0]);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_Location_details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Location_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col sm={3}>{t("I18N_Location_code")}:</Col>
                  { configInfo.uiConfig.visibleFields.LOCATION_Name ?
                    <Col sm={3}>{t("I18N_Name")}:</Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.LOCATION_NameLang2 ?
                    <Col sm={3}>{t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ")"}:</Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.LOCATION_NameLang3 ?
                    <Col sm={3}>{t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ")"}:</Col>
                  : null
                  }
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col sm={3}>{locationAttrs.code}</Col>
                  { configInfo.uiConfig.visibleFields.LOCATION_Name ?
                    <Col>
                      <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={locationAttrs.name}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.LOCATION_NameLang2 ?
                    <Col sm={3}>
                      <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="nameLang2" value={locationAttrs.nameLang2}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                  : null
                  }
                  { configInfo.uiConfig.visibleFields.LOCATION_NameLang3 ?
                    <Col sm={3}>
                      <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="nameLang3" value={locationAttrs.nameLang3}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                  : null
                  }
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col sm={3}>{t("I18N_Description")}: *</Col>
                  <Col sm={3}>{t("I18N_Type")}: *</Col>
                  <Col sm={3}>{t("I18N_Ext_sys_id")}:</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col sm={3}>
                    <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={locationAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col sm={3}>
                    <Dropdown>
                      <Dropdown.Toggle size="tiny_dropdown" variant="outline-secondary" className={classes.dropdown_item_wrap} id="dropdown-basic-button">
                        {!locationAttrs.type ? t("common:I18N_DROPDOWN_PROMPT") : t("common:I18N_LOC_TYPE_" + locationAttrs.type) }
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto"}} id="dropdown-basic-button" >
                        {typeList.map((item, index) => {
                          return (
                            <Dropdown.Item key={index} eventKey={item.code} className={classes.dropdown_item_wrap} onClick={() => onTypeClick(item.key)}>
                              {t("common:I18N_LOC_TYPE_" + item.value)}
                            </Dropdown.Item>
                          );
                        })}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  <Col sm={3}>
                    <input className={classes.admin_pages__field_editable} name="externalSystemId" value={locationAttrs.externalSystemId}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Site")}:</Col>
                  <Col>{t("I18N_Building")}:</Col>
                  <Col>{t("I18N_Wing")}: </Col>
                  <Col>{t("I18N_Floor")}:</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="site" value={locationAttrs.site}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="building" value={locationAttrs.building}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="wing" value={locationAttrs.wing}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="floor" value={locationAttrs.floor}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Room")}:</Col>
                  <Col>{t("I18N_Room_type")}:</Col>
                  <Col>{t("I18N_Room_seq")}:</Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="room" value={locationAttrs.room}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="roomType" value={locationAttrs.roomType}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} name="roomSeq" value={locationAttrs.roomSeq}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col></Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col sm={3}>{t("I18N_Location_code")}:</Col>
                { configInfo.uiConfig.visibleFields.LOCATION_Name ?
                  <Col sm={3}>{t("I18N_Name")}:</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.LOCATION_NameLang2 ?
                  <Col sm={3}>{t("I18N_Name") + " (" + t("custom:I18N_Language_2") + ")"}:</Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.LOCATION_NameLang3 ?
                  <Col sm={3}>{t("I18N_Name") + " (" + t("custom:I18N_Language_3") + ")"}:</Col>
                : null
                }
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col sm={3}>{locationAttrs.code}</Col>
                <Col sm={3}>{locationAttrs.name}</Col>
                { configInfo.uiConfig.visibleFields.LOCATION_NameLang2 ?
                  <Col sm={3}> {locationAttrs.nameLang2} </Col>
                : null
                }
                { configInfo.uiConfig.visibleFields.LOCATION_NameLang3 ?
                  <Col sm={3}> {locationAttrs.nameLang3} </Col>
                : null
                }
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col sm={3}>{t("I18N_Description")}:</Col>
                <Col sm={3}>{t("I18N_Type")}:</Col>
                <Col sm={3}>{t("I18N_Ext_sys_id")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col sm={3}>{locationAttrs.description}</Col>
                <Col sm={3}>{t("common:I18N_LOC_TYPE_" + locationAttrs.type)}</Col>
                <Col sm={3}>{locationAttrs.externalSystemId}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Site")}:</Col>
                <Col>{t("I18N_Building")}:</Col>
                <Col>{t("I18N_Wing")}:</Col>
                <Col>{t("I18N_Floor")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{locationAttrs.site}</Col>
                <Col>{locationAttrs.building}</Col>
                <Col>{locationAttrs.wing}</Col>
                <Col>{locationAttrs.floor}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Room")}:</Col>
                <Col>{t("I18N_Room_type")}:</Col>
                <Col>{t("I18N_Room_seq")}:</Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{locationAttrs.room}</Col>
                <Col>{locationAttrs.roomType}</Col>
                <Col>{locationAttrs.roomSeq}</Col>
                <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{locationAttrs.creatorUsername}</Col>
                <Col>{formatDate(locationAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{locationAttrs.updaterUsername}</Col>
                <Col>{formatDate(locationAttrs.timeUpdated)}</Col>
              </Row>

            </Container>
          }
        </div>


  

        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize:'12px', fontWeight:'300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateLocationErrMsg}
              </span>
*/}
            </div>
          </Form.Group>
        </div>

      </Card>
    </>
  );
}
 
export default LocationAdminDetailsView;
